import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  AXIOS_INSTANCE,
  fetchUserRoleGroupReason,
  grantRoleGroupToUser,
  patchGrantedRoleGroupToUser,
  useFetchUserRoleGroupAssignments
} from '@jobandtalent/auth-api-client-v2';
import {
  FetchRoleGroups200,
  FetchRoleGroups200DataRoleGroupsItem,
  UsersUsersItem
} from '@jobandtalent/auth-api-client-v2/dist/apiClient/model';
import { Select, Textarea } from '@jobandtalent/design-system';

import useSWRInfinite from 'swr/infinite';

import { formatDataScopesData, formatDataScopesInput } from 'src/common/dataScopesHelper';
import FormInput from 'src/components/FormInput/FormInput';
import SavedReason from 'src/components/SavedReason/SavedReason';
import StackView, { SaveCancelFooter } from 'src/components/StackView/StackView';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';

import BulkAssignmentSelect from '../BulkAssignments/BulkAssignmentSelect';
import useBulkAssignments from '../BulkAssignments/useBulkAssignments';
import ReasonField, { optionalReasonRules, reasonRules } from '../ReasonField/ReasonField';
import { UserRoleGroupAssignment } from '../Users.types';

interface NewRoleGroupFormProps {
  bulk?: boolean;
  onAssignmentsChanged?: () => void;
  onClose: () => void;
  onSuccess: () => void;
  selectedUser: UsersUsersItem;
  selectedRoleGroup?: UserRoleGroupAssignment | null;
}

interface RoleGroupOption {
  value: number;
  label: string;
}

interface RoleGroupFormValues {
  reason: string;
  roleGroup: RoleGroupOption | null;
  dataScopes: string;
}

interface KeyValuePair {
  key: string;
  value: string;
}

const formatContent = (content: string): string => content.replace(/, */g, ',\n');

const parseDataScopesInput = (input: string): KeyValuePair[] => input.split(',').map((pair) => {
  const [key, value] = pair.split(':').map((part) => part.trim());
  return { key, value };
});

const calculateHeight = (displayValue: string, defaultHeight: string): string => {
  const textarea = document.createElement('textarea');
  textarea.style.visibility = 'hidden';
  textarea.style.position = 'absolute';
  textarea.style.height = 'auto';
  textarea.style.whiteSpace = 'pre';
  textarea.value = displayValue;

  document.body.appendChild(textarea);
  const calculatedHeight = Math.max(textarea.scrollHeight, parseInt(defaultHeight));
  document.body.removeChild(textarea);

  return `${calculatedHeight}px`;
};

const fetcher = (input: RequestInfo): Promise<FetchRoleGroups200> =>
  AXIOS_INSTANCE({ url: input as string, method: 'GET' }).then((response) => response.data);

const getKey = (pageIndex: number, previousPageData: FetchRoleGroups200 | null) => {
  if (previousPageData && !previousPageData.meta.next_page) {
    return null;
  }

  return `/role_groups?page=${pageIndex + 1}&per_page=100`;
};

const NewRoleGroupForm = ({ onClose, onSuccess, selectedUser, selectedRoleGroup, bulk = false, onAssignmentsChanged }: NewRoleGroupFormProps) => {
  const {
    data: roleGroupsPages,
    error: roleGroupsError,
    setSize
  } = useSWRInfinite<FetchRoleGroups200>(getKey, fetcher, { revalidateFirstPage: false });
  const {
    data: userRoleGroupsData,
    error: userRoleGroupsError
  } = useFetchUserRoleGroupAssignments(selectedUser.id);
  const { error, setError } = useError();
  const bulkAssignments = useBulkAssignments();
  const { permissions, error: permissionError } = useLocalPermissions();
  const { control, handleSubmit } = useForm<RoleGroupFormValues>({
    defaultValues: {
      roleGroup: selectedRoleGroup ? { value: selectedRoleGroup.role_group_id, label: selectedRoleGroup.role_group_name } : null,
      reason: ''
    }
  });
  const [dataScopeValue, setDataScopeValue] = useState(formatDataScopesInput(selectedRoleGroup?.data_scope ?? []));
  const [dataPairs, setDataPairs] = useState<KeyValuePair[]>([]);
  const [shouldRenderDataPairs, setShouldRenderDataPairs] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const lastPage = roleGroupsPages?.at(-1);
    if (lastPage?.meta.next_page) {
      setSize((currentSize) => currentSize + 1);
    }
  }, [roleGroupsPages]);

  const roleGroupOptions = useMemo(() => {
    if (!roleGroupsPages || !userRoleGroupsData) {
      return [];
    }

    const assignedGroupIds = new Set(
      userRoleGroupsData.data.user_role_groups.map(({ role_group_id }) => role_group_id)
    );
    const roleGroups = roleGroupsPages.reduce<FetchRoleGroups200DataRoleGroupsItem[]>(
      (allRoleGroups, page) => [...allRoleGroups, ...page.data.role_groups],
      []
    );

    return roleGroups
      .filter(({ id, assignment_restricted }) => !assignedGroupIds.has(id) && !assignment_restricted)
      .map(({ id, name }) => ({
        value: id,
        label: name
      }));
  }, [roleGroupsPages, userRoleGroupsData]);

  useEffect(() => {
    const requestError = roleGroupsError || userRoleGroupsError;
    if (requestError) {
      setError(requestError?.response?.data ? requestError.response.data : DEFAULT_ERROR);
    }
  }, [roleGroupsError, userRoleGroupsError]);

  const onSubmit = async ({ roleGroup, reason }: RoleGroupFormValues) => {
    if (bulk && !selectedRoleGroup) {
      const shared = { reason: reason.trim(), data_scope: formatDataScopesData(dataScopeValue) };
      const complete = await bulkAssignments.submit((option) => grantRoleGroupToUser(selectedUser.id, {
        user_role_group: { ...shared, role_group_id: option.value }
      }), onAssignmentsChanged);
      if (complete) onSuccess();
      return;
    }
    if (!roleGroup) {
      return;
    }

    setIsSaving(true);
    const request = selectedRoleGroup
      ? patchGrantedRoleGroupToUser(selectedUser.id, selectedRoleGroup.role_group_id, {
        user_role_group: {
          data_scope: formatDataScopesData(dataScopeValue),
          ...(reason.trim() ? { reason: reason.trim() } : {})
        }
      })
      : grantRoleGroupToUser(selectedUser.id, {
        user_role_group: {
          role_group_id: roleGroup.value,
          reason: reason.trim(),
          data_scope: formatDataScopesData(dataScopeValue)
        }
      });
    request.then(onSuccess, (requestError) => {
      setIsSaving(false);
      setError(requestError?.response?.data ? requestError.response.data : DEFAULT_ERROR);
    });
  };

  const handlePairChange = (index: number, keyOrValue: 'key' | 'value', newValue: string) => {
    const updatedPairs = dataPairs.map((pair, pairIndex) =>
      pairIndex === index ? { ...pair, [keyOrValue]: newValue } : pair
    );
    setDataPairs(updatedPairs);
    setDataScopeValue(updatedPairs.map(({ key, value }) => `${key}:${value}`).join(','));
  };

  const keyValuePattern = /\w+:\w+/;

  const closeForm = () => {
    if (!bulkAssignments.isSaving) onClose();
  };

  const StackViewFooter = (
    <SaveCancelFooter
      isCancelDisabled={bulkAssignments.isSaving}
      isSaveDisabled={isSaving || (bulk && (bulkAssignments.isSaving || !bulkAssignments.selected.length))}
      onClose={closeForm}
      onSelect={handleSubmit(onSubmit)}
    />
  );

  return (
    <StackView footer={StackViewFooter} onClose={closeForm} nested title={selectedRoleGroup ? "Edit role group" : bulk ? "Bulk add role groups" : "Grant New Roles Group"}>
      <FormInput label={bulk ? "Role groups" : "Role Group name"}>
        {bulk && !selectedRoleGroup ? (
          <BulkAssignmentSelect options={roleGroupOptions} assignments={bulkAssignments} />
        ) : (
          <Controller
            name="roleGroup"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                isDisabled={!!selectedRoleGroup}
                value={value}
                onChange={(option: RoleGroupOption | null) => {
                  onChange(option);
                  setDataScopeValue('');
                  setDataPairs([]);
                  setShouldRenderDataPairs(false);
                }}
                options={roleGroupOptions}
              />
            )}
          />
        )}
      </FormInput>
      <div>
        <label htmlFor="data-scopes" className="ds-text-body-base-semibold">Data Scopes</label>
        {shouldRenderDataPairs && dataPairs.map((pair, index) => (
          <div className="data-scope-block" key={index}>
            <input
              name="data-scope-key"
              type="text"
              value={pair.key}
              onChange={(event) => handlePairChange(index, 'key', event.target.value)}
            />
            <input
              name="data-scope-value"
              type="text"
              value={pair.value}
              onChange={(event) => handlePairChange(index, 'value', event.target.value)}
            />
          </div>
        ))}
        <Controller
          name="dataScopes"
          control={control}
          rules={selectedRoleGroup ? undefined : {
            validate: () => formatDataScopesData(dataScopeValue).length > 0 || 'Data scope is required.'
          }}
          render={({ field: { onChange }, fieldState }) => (
            <Textarea
              name="data-scopes"
              height={calculateHeight(formatContent(dataScopeValue), '87')}
              onChange={(event) => {
                const formattedDataScope = formatDataScopesInput(formatDataScopesData(event.value));
                setDataScopeValue(formattedDataScope);
                setDataPairs(parseDataScopesInput(event.value));
                setShouldRenderDataPairs(keyValuePattern.test(event.value));
                onChange(event.value);
              }}
              value={formatContent(dataScopeValue)}
              error={fieldState.error?.message}
              placeholder="Data scopes must be separated by commas. i.e., company_id:1, company_id:2, country:ES"
            />
          )}
        />
      </div>
      {selectedRoleGroup && (
        <SavedReason
          canReadReason={!permissionError && permissions.readAssignmentDetailsPermission}
          loadReason={() => fetchUserRoleGroupReason(selectedUser.id, selectedRoleGroup.role_group_id)}
        />
      )}
      <Controller
        name="reason"
        control={control}
        defaultValue=""
        rules={selectedRoleGroup ? optionalReasonRules : reasonRules}
        render={({ field, fieldState }) => (
          <ReasonField
            value={field.value}
            onChange={field.onChange}
            inputRef={field.ref}
            error={fieldState.error}
            append={!!selectedRoleGroup}
            placeholder={selectedRoleGroup ? "Write an additional reason to append" : "Write why this user needs this group"}
          />
        )}
      />
      {error}
    </StackView>
  );
};

export default NewRoleGroupForm;
